import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import back_app from '../../lib/back_app';
import { cat_id2lbl } from '../../lib/cat';
import BrandHeader from '../../components/brand/BrandHeader';

import xdate from '../../../../../util/date.js';

type Vcamp = {
  id: string;
  user_id: string;
  org_id: string;
  campaign_id: string;
  video_id: string;
  ad_type?: string;
  creator_tier?: string;
  processing_status?: string;
  processed_video_url?: string;
  placement?: {timestamp: number, ad_type: string};
  payment_amount?: number;
  payment_tx_id?: string;
  published_url?: string;
  published_status?: string;
  published_ts?: string;
  published_platform?: string;
  views?: {cnt: number, ts: string, err?: string};
  track_until?: string;
  reject_reason?: string;
  ts?: {insert: string, modified: string};
  videos?: {
    id: string;
    title: string;
    description?: string;
    video_url: string;
    thumbnail_url?: string;
    duration: number;
    category?: string;
    tags?: string[];
  };
  campaigns?: {
    id: string;
    name: string;
  };
};

export default function CreatorVcamp() {
  const navigate = useNavigate();
  const { campaign_id, vcamp_id } = useParams();
  const [vcamp, set_vcamp] = useState<Vcamp | null>(null);
  const [loading, set_loading] = useState(true);
  const [err, set_err] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await back_app.vcamp_get(vcamp_id!);
        if (res?.err)
          set_err(res.err);
        else
          set_vcamp(res);
      } catch (e: any) {
        if (e?.message == 'no session')
          navigate('/login');
        else
          set_err(e?.message || 'failed');
      }
      set_loading(false);
    })();
  }, [vcamp_id]);

  const back_to_camp = () => navigate(`/brand/campaigns/${campaign_id}`);

  if (loading)
  {
    return (
      <div className="min-h-screen bg-gray-50">
        <BrandHeader />
        <div className="flex items-center justify-center h-96">
          <div className="inline-block w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (err || !vcamp)
  {
    return (
      <div className="min-h-screen bg-gray-50">
        <BrandHeader />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {err == 'forbidden' ? 'Not allowed' : 'Creator video not found'}
          </h2>
          <button
            onClick={back_to_camp}
            className="text-blue-600 hover:underline"
          >
            Back to Campaign
          </button>
        </div>
      </div>
    );
  }

  const processed = vcamp.processed_video_url;
  const original_url = vcamp.videos?.video_url;
  const placement_ts = vcamp.placement?.timestamp;
  const status_color = vcamp.processing_status == 'completed'
    ? 'bg-green-100 text-green-700'
    : vcamp.processing_status == 'processing'
      || vcamp.processing_status == 'analyzing'
      ? 'bg-yellow-100 text-yellow-700'
      : 'bg-gray-100 text-gray-700';
  const pub_color = vcamp.published_status == 'pending_review'
    || vcamp.published_status == 'tracking'
    ? 'bg-blue-100 text-blue-700'
    : vcamp.published_status == 'approved'
      ? 'bg-green-100 text-green-700'
      : vcamp.published_status == 'rejected'
        ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700';

  return (
    <div className="min-h-screen bg-gray-50">
      <BrandHeader />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <button
          onClick={back_to_camp}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          <span>←</span>
          <span>Back to Campaign</span>
        </button>

        <div className="bg-white rounded-xl border shadow-sm p-4 sm:p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {vcamp.videos?.title || 'Untitled Video'}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Creator: <span className="font-medium">{vcamp.user_id}</span>
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${status_color}`}>
                {vcamp.processing_status || 'unknown'}
              </span>
              {vcamp.published_status && (
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${pub_color}`}>
                  {vcamp.published_status}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {}
          <div className="bg-white rounded-xl border shadow-sm p-4 sm:p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span className="text-2xl">✨</span> With Ad
            </h3>
            {processed ? (
              <div className="bg-black rounded-lg overflow-hidden mx-auto" style={{maxWidth: '400px'}}>
                <video
                  src={processed}
                  controls
                  className="w-full"
                  style={{aspectRatio: '9/16'}}
                />
              </div>
            ) : (
              <div className="text-sm text-gray-500 py-8 text-center">
                Not yet processed.
              </div>
            )}
          </div>

          {}
          <div className="bg-white rounded-xl border shadow-sm p-4 sm:p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span className="text-2xl">🎬</span> Original
            </h3>
            {original_url ? (
              <div className="bg-black rounded-lg overflow-hidden mx-auto" style={{maxWidth: '400px'}}>
                <video
                  src={original_url}
                  controls
                  className="w-full"
                  style={{aspectRatio: '9/16'}}
                />
              </div>
            ) : (
              <div className="text-sm text-gray-500 py-8 text-center">
                Original unavailable.
              </div>
            )}
          </div>
        </div>

        {}
        <div className="bg-white rounded-xl border shadow-sm p-4 sm:p-6 mb-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Details</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-gray-600">Ad Type</div>
              <div className="font-semibold text-gray-900">
                {vcamp.ad_type == 'break' ? 'Video Break' :
                  vcamp.ad_type == 'logo' ? 'Logo Overlay' : (vcamp.ad_type || '—')}
              </div>
            </div>
            <div>
              <div className="text-gray-600">Creator Tier</div>
              <div className="font-semibold text-gray-900">
                {vcamp.creator_tier || '—'}
              </div>
            </div>
            {placement_ts !== undefined && (
              <div>
                <div className="text-gray-600">Ad Placement</div>
                <div className="font-semibold text-gray-900">
                  {Math.floor(placement_ts / 60)}:{String(Math.floor(placement_ts % 60)).padStart(2, '0')}
                </div>
              </div>
            )}
            {vcamp.videos?.duration && (
              <div>
                <div className="text-gray-600">Video Duration</div>
                <div className="font-semibold text-gray-900">
                  {Math.floor(vcamp.videos.duration / 60)}:{String(Math.floor(vcamp.videos.duration % 60)).padStart(2, '0')}
                </div>
              </div>
            )}
            {vcamp.videos?.category && (
              <div>
                <div className="text-gray-600">Category</div>
                <div className="font-semibold text-gray-900">{cat_id2lbl(vcamp.videos.category)}</div>
              </div>
            )}
            {vcamp.ts?.insert && (
              <div>
                <div className="text-gray-600">Joined</div>
                <div className="font-semibold text-gray-900">
                  {xdate.fmt_lbl_short(vcamp.ts.insert)}
                </div>
              </div>
            )}
            {vcamp.views && (
              <div>
                <div className="text-gray-600">Views</div>
                <div className="font-semibold text-gray-900">
                  {vcamp.views.cnt.toLocaleString()} / 1,000
                  {vcamp.published_platform && ` (${vcamp.published_platform})`}
                </div>
              </div>
            )}
            {vcamp.published_status == 'tracking' && vcamp.track_until && (
              <div>
                <div className="text-gray-600">Tracking until</div>
                <div className="font-semibold text-gray-900">
                  {xdate.fmt_lbl_short(vcamp.track_until)}
                </div>
              </div>
            )}
            {vcamp.reject_reason && (
              <div>
                <div className="text-gray-600">Rejected</div>
                <div className="font-semibold text-gray-900">
                  {vcamp.reject_reason == 'views_below_min'
                    ? 'Under 1,000 views in 14 days' : vcamp.reject_reason}
                </div>
              </div>
            )}
            {vcamp.payment_amount !== undefined && (
              <div>
                <div className="text-gray-600">Payment</div>
                <div className="font-semibold text-gray-900">
                  ${vcamp.payment_amount.toLocaleString()}
                </div>
              </div>
            )}
            {vcamp.payment_tx_id && (
              <div className="sm:col-span-2">
                <div className="text-gray-600">Payment Tx</div>
                <div className="font-mono text-xs text-gray-700 break-all">
                  {vcamp.payment_tx_id}
                </div>
              </div>
            )}
          </div>
          {vcamp.videos?.description && (
            <div className="mt-4 pt-4 border-t">
              <div className="text-gray-600 text-sm mb-1">Description</div>
              <p className="text-gray-900 text-sm leading-relaxed">
                {vcamp.videos.description}
              </p>
            </div>
          )}
          {!!vcamp.videos?.tags?.length && (
            <div className="mt-4 pt-4 border-t">
              <div className="text-gray-600 text-sm mb-2">Tags</div>
              <div className="flex flex-wrap gap-2">
                {vcamp.videos.tags.map((t, i) => (
                  <span key={i} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                    {t}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {}
        {vcamp.published_url && (
          <div className="bg-white rounded-xl border shadow-sm p-4 sm:p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-3">Published</h3>
            <a
              href={vcamp.published_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline break-all"
            >
              {vcamp.published_url}
            </a>
            {vcamp.published_ts && (
              <div className="text-xs text-gray-500 mt-1">
                {xdate.fmt_lbl_short(vcamp.published_ts)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}